import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, UserCheck, CloudDownload, Trash2, HandMetal, Info, FileEdit } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrgSettings } from '../contexts/OrgSettingsContext';
import { REQUESTTYPES } from './constant';
import { useDsrByEmail } from '../hooks/usePrivacyQueries';
import { DropdownMenu, type DropdownMenuItem } from './ui';

export const Header: React.FC = () => {
  const { logout: authLogout } = useAuth();
  const { logoPath, headerColor, headerTextColor } = useOrgSettings();

  const logout = () => {
    authLogout();
    window.location.href = '/';
  };

  return (
    <header
      className="h-12 sticky top-0 z-50 shadow-sm flex items-center px-4 sm:px-6"
      style={{ backgroundColor: headerColor }}
    >
      <div className="flex items-center justify-between w-full max-w-6xl mx-auto">
        {/* Logo */}
        <Link to="/" className="flex items-center shrink-0 h-12">
          {logoPath ? (
            <img
              alt="Logo"
              src={logoPath}
              className="h-7 max-w-[120px] sm:max-w-[140px] object-contain"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-primary/20" />
          )}
        </Link>

        {/* Center title */}
        <div
          className="absolute left-1/2 -translate-x-1/2 text-base sm:text-lg font-semibold hidden sm:block"
          style={{ color: headerTextColor }}
        >
          Privacy Center
        </div>

        {/* Right side: notifications + user menu */}
        <div className="flex items-center gap-1">
          <LoggedInUser logout={logout} headerTextColor={headerTextColor} />
        </div>
      </div>
    </header>
  );
};

interface LoggedInUserProps {
  logout: () => void;
  headerTextColor: string;
}

function LoggedInUser({ logout, headerTextColor }: LoggedInUserProps) {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated && user) {
    const userMenuItems: DropdownMenuItem[] = [
      { key: 'email', label: user.email },
      { key: 'signOut', label: 'Sign out', onClick: logout },
    ];

    return (
      <div className="flex items-center gap-1">
        <UserDataSubjectRequest headerTextColor={headerTextColor} />
        <DropdownMenu
          trigger={
            <button
              className="p-2 rounded-md hover:bg-white/10 transition-colors"
              title="User"
              aria-label="User"
            >
              <UserCheck className="h-5 w-5" style={{ color: headerTextColor }} />
            </button>
          }
          items={userMenuItems}
        />
      </div>
    );
  }

  return null;
}

interface UserDsrProps {
  headerTextColor: string;
}

function UserDataSubjectRequest({ headerTextColor }: UserDsrProps) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: dsrResponse } = useDsrByEmail(user?.email || '', !!user?.email);

  const getIcon = (requestType: string) => {
    switch (requestType) {
      case REQUESTTYPES.DOWNLOAD:
        return <CloudDownload className="h-4 w-4 text-gray-500" />;
      case REQUESTTYPES.ERASE:
        return <Trash2 className="h-4 w-4 text-gray-500" />;
      case REQUESTTYPES.DONOTSELL:
        return <HandMetal className="h-4 w-4 text-gray-500" />;
      case REQUESTTYPES.CONSENT:
        return <Info className="h-4 w-4 text-gray-500" />;
      default:
        return <FileEdit className="h-4 w-4 text-gray-500" />;
    }
  };

  const dsrDataItems: DropdownMenuItem[] = (dsrResponse?.dataSubjects || []).map((item: any) => ({
    key: item.id,
    label: item.requestType,
    secondaryText: new Date(item.createdDate).toLocaleString(),
    icon: getIcon(item.requestType),
  }));

  dsrDataItems.push({
    key: 'viewAll',
    label: 'View All',
    onClick: () => navigate('/datasubjectrequestlist'),
  });

  const menuItems: DropdownMenuItem[] =
    dsrDataItems.length > 1 ? dsrDataItems : [{ key: 'loading', label: 'Loading...' }];

  return (
    <DropdownMenu
      trigger={
        <button
          className="p-2 rounded-md hover:bg-white/10 transition-colors"
          title="Notifications"
          aria-label="Notifications"
        >
          <Bell className="h-5 w-5" style={{ color: headerTextColor }} />
        </button>
      }
      items={menuItems}
    />
  );
}
